import React, { ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Button,
  ButtonProps,
  Stack,
  StackProps,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MyMenuContent, MyMenuContentTitle } from '@/constant/mymenu';
import Image from 'next/image';
import AngleBracketIcon from '@/public/icons/angle_bracket.svg';
import { Passport } from '@/types/api';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import { defaultImageUrl } from '@/components/MenuDrawer/MyMenuDrawer/@components/Profile';
import { useTranslation } from 'react-i18next';

export const MypageContentSx: SxProps<Theme> = {
  '.dot': {
    width: '4px',
    height: '4px',
    backgroundColor: 'var(--dk-bg, white)',
    borderRadius: '100%',
  },
  '.profile': {
    marginTop: '12px',
    fontSize: '14px',
    color: 'var(--dk-text-dim, #B7BBC2)',
    '.name': { fontWeight: 500, fontSize: '20px', color: 'white' },
    '.nickname': {
      fontSize: '16px',
      color: 'white',
    },
  },
  '.jobWrap': {
    margin: '28px 20px 0',
    padding: '12px',
    borderRadius: '4px',
    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
    '.job': {
      width: 'fit-content',
      margin: 0,
      padding: '2px 8px',
      border: '1px solid #888D96',
      borderRadius: '4px',
      backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
      fontSize: '12px',
      color: 'var(--dk-text, black)',
      textTransform: 'uppercase',
    },
    '.empty': {
      margin: 0,
      textAlign: 'center',
      letterSpacing: '-0.05px', // 디자인 그대로 적용 시 내용이 넘침
      fontSize: '14px',
      color: 'var(--dk-text-dim, #B7BBC2)',
    },
    '.summary': { margin: '4px 0 0', fontSize: '14px' },
    '.content': {
      margin: '8px 0 0',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      fontSize: '12px',
      color: 'var(--dk-text-secondary, #888D96)',
    },
  },
  'button.edit-profile': {
    display: 'block',
    padding: 0,
    margin: '8px 20px 0 auto',
    textDecoration: 'underline',
    color: 'var(--dk-text, #000)',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '20px',
  },
  section: {
    padding: '24px 20px',
    borderTop: '8px solid var(--dk-line, #F9FAFB)',
    h2: {
      margin: 0,
      padding: '0 4px',
      fontWeight: 400,
      fontSize: '14px',
      color: 'var(--dk-text-secondary, #888D96)',
    },
  },
};

export const ProfileAccordion = styled(
  ({
    defaultPassportId = '',
    profileList = [],
    children,
    ...props
  }: AccordionProps & {
    defaultPassportId?: string;
    profileList?: Passport[];
  }) => {
    const { t } = useTranslation();
    return (
      <>
        {profileList.length > 1 ? (
          <Accordion disableGutters square {...props}>
            <AccordionSummary
              expandIcon={<AngleBracketIcon width={'24px'} height={'24px'} />}
              aria-controls="profile-panel"
              id="profile-panel"
            >
              <Typography component="span">
                {t('MYPAGE.프로필 전환')}
              </Typography>
              <Stack direction={'row'} className={'preview-profile'}>
                {[...profileList]
                  ?.sort((a, b) => {
                    if (a.passportId === defaultPassportId) return -1;
                    if (b.passportId === defaultPassportId) return 1;
                    return 0; // 원래 순서 유지
                  })
                  .slice(0, 3)
                  ?.map((item, index) => (
                    <ImageWithErrorHandling
                      key={index}
                      src={imgUrlChk(
                        item.detail.profileImageUrl || defaultImageUrl,
                      )}
                      width={32}
                      height={32}
                      alt={`${item.detail.name} Profile Image}`}
                    />
                  ))}
                {profileList && profileList.length > 3 && (
                  <span>+{profileList?.length - 2}</span>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>{children}</AccordionDetails>
          </Accordion>
        ) : (
          <div style={{ marginTop: '27px' }} />
        )}
      </>
    );
  },
)({
  '&.MuiAccordion-root': {
    border: 'none',
    boxShadow: 'none',
    '&::before': { display: 'none' },
  },
  '.MuiAccordion-gutters': {
    backgroundColor: 'red',
  },
  '.MuiAccordionSummary-root': {
    padding: '16px 20px',
    borderTop: '1px solid var(--dk-line, #E7E9EC)',
    '.MuiAccordionSummary-content': {
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: 0,
      fontSize: '16px',
      '.preview-profile': {
        alignItems: 'center',
        padding: '0 12px',
        img: {
          marginLeft: '-8px',
          borderRadius: '100%',
          border: '1px solid var(--dk-bg, white)',
          objectFit: 'cover',
        },
        span: {
          display: 'inline-block',
          width: '32px',
          height: '32px',
          backgroundColor: 'rgba(255, 255,255, 0.8)',
          marginLeft: '-32px',
          fontSize: '12px',
          color: 'var(--dk-text-strong, #5F636B)',
          borderRadius: '100%',
          lineHeight: '32px',
          textAlign: 'center',
        },
      },
    },
    '&.Mui-expanded': {
      '.preview-profile': { opacity: 0 },
    },
  },
  '.MuiAccordionDetails-root': {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '24px 16px',
    borderTop: '1px solid var(--dk-line, #E7E9EC)',
  },
});

export const ProfileItem = styled(
  ({
    profile,
    children,
    sx,
    ...props
  }: StackProps & ButtonProps & { profile: Passport }) => {
    const { t } = useTranslation();
    return (
      <Stack
        component={'button'}
        direction={'row'}
        alignItems={'center'}
        sx={{
          textAlign: 'left',
          padding: '4px',
          borderRadius: '4px',
          '.profile-image-wrap': {
            width: '38px',
            height: '38px',
            borderRadius: '100%',
            marginRight: '8px',
            padding: '2px',
            'img.profile-image': {
              borderRadius: '100%',
              objectFit: 'cover',
            },
          },
          '.profile-title': {
            flex: 1,
            margin: 0,
            marginRight: '4px',
            fontSize: '14px',
            span: {
              fontSize: '12px',
              color: 'var(--dk-text-secondary, #888D96)',
              '::before': {
                content: '""',
                display: 'inline-block',
                width: '2px',
                height: '2px',
                margin: '0 4px',
                backgroundColor: '#888D96',
                borderRadius: '100%',
                verticalAlign: 'middle',
              },
            },
          },
          '&.selected': {
            '.profile-image-wrap': {
              border: '1px solid var(--dk-accent, #0066CC)',
            },
            '&::after': {
              content: `"${t('MYPAGE.현재 프로필')}"`,
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '999px',
              backgroundColor: 'rgba(0, 102, 204, 0.12)',
              fontSize: '12px',
              color: 'var(--dk-accent, #0066CC)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          },
          ...sx,
        }}
        {...props}
      >
        <Stack
          justifyContent={'center'}
          alignItems={'center'}
          className={'profile-image-wrap'}
        >
          <ImageWithErrorHandling
            src={imgUrlChk(profile.detail.profileImageUrl)}
            width={32}
            height={32}
            alt={'profile-image'}
            className={'profile-image'}
          />
        </Stack>
        <p className={'profile-title ellipsis'}>
          {profile.detail.companyName}
          <span>{profile.detail.departmentName}</span>
        </p>
      </Stack>
    );
  },
)({});

export type SubMenuButtonProps = {
  icon?: ReactNode;
  menu: MyMenuContent;
} & ButtonProps;
export const SubMenuButton = styled(
  ({ icon, menu, content, sx, ...props }: SubMenuButtonProps) => {
    const { t } = useTranslation();
    return (
      <Button
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          padding: '4px',
          '.menu-name': {
            flex: 1,
            textAlign: 'left',
            fontSize: '16px',
            color: 'var(--dk-text, black)',
          },
          '.menu-content': {
            fontSize: '14px',
            color: 'var(--dk-text-secondary, #888D96)',
          },
          ...sx,
        }}
        {...props}
      >
        <Image
          alt={`${menu}-icon`}
          src={`/icons/mypage/${menu}.svg`}
          width={20}
          height={20}
        />
        <Typography className={'menu-name'}>
          {t(`MYPAGE.${MyMenuContentTitle[menu]}`)}
        </Typography>
        {content && (
          <Typography className={'menu-content'}>{content}</Typography>
        )}
      </Button>
    );
  },
)({});
